import { useMemo, useRef } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { useActiveView } from "@/hooks/useActiveView";
import { useHomeLocation } from "@/hooks/useHomeLocation";

import { homePath } from "@/utils/UHomePath";

import { NEW_TASK } from "@/constants/CTasks";

import { TNewTaskPrefill } from "@/types";

type TNavigateOptions = { replace?: boolean };

/**
 * Every move on the home page, as a URL change. Keeps the tasks period
 * (whatever else the query holds) across moves; changing view or
 * project closes the task panel; a task move keeps the project filter.
 *
 * The helpers are stable (one object for the component's life) and read
 * the current location through a ref: safe to hand to memoised children
 * or to capture in a callback.
 */
export const useHomeNavigation = () => {
	const navigate = useNavigate();
	const [search] = useSearchParams();
	const { view } = useActiveView();
	const { projectId } = useHomeLocation();

	const current = useRef({ search, viewId: view?.id, projectId });
	current.current = { search, viewId: view?.id, projectId };

	return useMemo(() => {
		const go = (path: string, options?: TNavigateOptions) =>
			navigate(path, options);
		const now = () => current.current;

		return {
			/** No id: "/", the default view. */
			toView: (viewId?: number, options?: TNavigateOptions) =>
				go(homePath({ viewId }, now().search), options),
			/** Filters on a project; the view stays unless `viewId` says otherwise. */
			toProject: (
				projectId: number,
				opts?: TNavigateOptions & { viewId?: number }
			) =>
				go(
					homePath(
						{ viewId: opts?.viewId ?? now().viewId, projectId },
						now().search
					),
					opts
				),
			/** The view without a project filter. */
			toAll: (viewId?: number, options?: TNavigateOptions) =>
				go(
					homePath({ viewId: viewId ?? now().viewId }, now().search),
					options
				),
			/** Under the current view unless `viewId` says otherwise (a task of another view). */
			toTask: (
				taskId: number,
				opts?: TNavigateOptions & {
					edit?: boolean;
					viewId?: number;
				}
			) =>
				go(
					homePath(
						{
							viewId: opts?.viewId ?? now().viewId,
							projectId: now().projectId,
							taskId,
							edit: opts?.edit,
						},
						now().search
					),
					opts
				),
			/** The current view's, unless `viewId` says otherwise (a sidebar project). */
			toNewTask: (
				prefill?: TNewTaskPrefill,
				opts?: TNavigateOptions & { viewId?: number }
			) =>
				go(
					homePath(
						{
							viewId: opts?.viewId ?? now().viewId,
							taskId: NEW_TASK,
							prefill: { projectId: now().projectId, ...prefill },
						},
						now().search
					),
					opts
				),
			/** Closes the task panel: view and project stay. */
			closeTask: (options?: TNavigateOptions) =>
				go(
					homePath(
						{
							viewId: now().viewId,
							projectId: now().projectId,
						},
						now().search
					),
					options ?? { replace: true }
				),
		};
	}, [navigate]);
};
